import { useMemo } from 'react';
import { useBrandId } from 'AppProvider';
import { CartItemState } from 'types/cart';
import useCartItems from 'hooks/useCartItems';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import useSWR from 'swr';
import { IExperiencePayload } from 'features/ShoppingCart/types';
import fetchExperienceList from 'api/experience/fetchExperienceList';

const EMPTY_ARRAY: IExperiencePayload = [];

const useFetchExperienceList = () => {
  const brandId = useBrandId();
  const { cartItems } = useCartItems();
  const languageCode = useSupportedLanguage();

  const cartItemKeys = useMemo(
    () => Object.keys(cartItems).sort().join(','),
    [cartItems],
  );

  const experienceIds = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(cartItems).flatMap(({ experienceId, state }) =>
            state === CartItemState.READY && experienceId ? experienceId : [],
          ),
        ),
      ).sort(),
    [cartItemKeys],
  );

  const key =
    experienceIds.length > 0
      ? JSON.stringify({ brandId, experienceIds, languageCode })
      : null;

  const { data, error, isLoading } = useSWR(key, fetchExperienceList, {
    revalidateOnFocus: false, // Don't refetch when user switches browser tabs back - too aggressive for cart
    revalidateOnReconnect: false, // Don't refetch when internet reconnects
    revalidateIfStale: true, // Auto-refetch if data is marked as "stale"
    dedupingInterval: 60000, // Within 60s, ignore duplicate requests with same key
    focusThrottleInterval: 300000, // Only revalidate on focus every 5 min
    refreshInterval: 0, // Don't poll
  });

  return {
    isError: !!error,
    isFetchingList: isLoading,
    experienceList: data ?? EMPTY_ARRAY,
  };
};

export default useFetchExperienceList;
